import React, { useMemo, useRef, useState } from "react";
import { Hatch } from "./ui/Hatch";
import { IMAGE_HEIGHT, IMAGE_HINT, IMAGE_MIME_TYPES, IMAGE_WIDTH } from "./broadcastEnums";
import { useMediaUrl } from "../../../hooks/comms/useMediaUrl";

/**
 * The broadcast image control: pick, drop, preview, remove.
 *
 * ---------------------------------------------------------------------------
 * WHY THE PREVIEW SOURCE IS NOT SIMPLY `dataUrl`
 * ---------------------------------------------------------------------------
 * The presign response's `data_url` is the blob's IDENTITY, not a fetchable
 * link. The media container is private, and a direct `GET` against that URL
 * answers `409 Public access is not permitted`. Rendering
 * `<img src={dataUrl}>` for a server-side URL therefore paints a broken image
 * — silently, because a broken `<img>` looks a lot like an image that has not
 * loaded yet.
 *
 * A fetchable URL only exists as a signed (SAS) URL minted by the media-url
 * endpoint, and it expires in roughly fifteen minutes. That is the whole
 * reason `useMediaUrl` exists, and the reason its cache is keyed on
 * `blob_path` rather than on the signed URL: the blob path is stable, the
 * signed URL is a fifteen-minute lease on it.
 *
 * So there are exactly two legitimate preview sources:
 *
 *   1. A LOCAL object URL (`blob:` / `data:`) for the file the author just
 *      picked. That one is real, immediate, and never needs the network.
 *   2. Anything else — resolve through `useMediaUrl(blobPath)`.
 *
 * This component is purely presentational. It fetches nothing beyond the
 * signed URL, owns no upload state, and reports every gesture upward through
 * `onPick` / `onClear`.
 */

/**
 * The only URL schemes safe to hand straight to `<img src>`.
 * `blob:` is `URL.createObjectURL(file)`; `data:` is an inlined read of the
 * same local file. Everything else is a server address that needs signing.
 */
const LOCAL_PREVIEW_SCHEME = /^(blob:|data:)/i;

/** The dashed box. Sized by its contents so no state changes its height. */
const BOX_BASE =
  "flex items-center gap-3 rounded-lg border border-dashed p-3 transition-colors";
const BOX_RESTING = "border-bc-dashed";
const BOX_DRAGGING = "border-orange-primary bg-light-orange";

/**
 * The media slot's footprint. Both the hatch placeholder and the thumbnail use
 * it, which is what keeps "empty", "uploading" and "has an image" the same
 * height — the box never jumps under the author's cursor.
 */
const MEDIA_SLOT = "h-[72px] w-28 shrink-0";

const BUTTON_BASE =
  "rounded-md border border-bc-border px-2.5 py-1 text-xs font-medium " +
  "transition-colors focus:outline-none focus:border-orange-primary";
const BUTTON_ENABLED = "text-bc-text-2 hover:border-orange-primary hover:text-bc-accent-text";
const BUTTON_DISABLED = "text-bc-faintest cursor-not-allowed";

/**
 * The note shown when the server has no image-upload scope at all.
 *
 * Deliberately inert: no dashed box, no button, no file input. A disabled-
 * looking dropzone invites a click that cannot work, and the author would read
 * the silence as a bug in their browser rather than as a missing capability.
 *
 * IMPORTANT: this state is NOT an error. The composer must remain fully
 * sendable while it is showing — an image was always optional, and a server
 * that cannot take one must not be able to block a broadcast.
 *
 * @returns {React.ReactElement}
 */
const UnsupportedNote = () => (
  <div
    data-testid="bc-image-unsupported"
    className="rounded-lg border border-bc-hairline bg-bc-subtle px-3 py-2.5 text-xs text-bc-soft"
  >
    Image upload isn&apos;t available on this server yet. You can still send
    this broadcast without an image.
  </div>
);

/**
 * The image control for the broadcast composer.
 *
 * @param {Object} props
 * @param {string} [props.previewUrl] - A local `blob:` URL for the file just picked. Preferred over everything else: it needs no network and cannot 404.
 * @param {string} [props.dataUrl] - Preview URL. Used directly ONLY when it is a local `blob:`/`data:` URL; a server `data_url` is an identity, not a fetchable link.
 * @param {string} [props.blobPath] - Storage path of the uploaded image. Resolved to a signed URL via `useMediaUrl`.
 * @param {string} [props.fileName] - Name of the picked file, shown beside the thumbnail.
 * @param {boolean} [props.uploading=false] - An upload is in flight; Browse is disabled.
 * @param {string} [props.error] - Blocking message. Announced assertively.
 * @param {string} [props.warning] - Advisory message, e.g. wrong dimensions. The image still uploaded, so it is NOT announced.
 * @param {boolean} [props.unsupported=false] - The backend rejected the upload scope entirely; the dropzone is replaced by an explanatory note.
 * @param {(file: File) => void} [props.onPick] - Called with the chosen `File`. Never called without one.
 * @param {() => void} [props.onClear] - Called with no arguments when the author removes the image.
 * @param {string} [props.className] - Extra classes on the wrapper, merged last.
 * @returns {React.ReactElement}
 */
export const ImageDropzone = ({
  dataUrl = "",
  previewUrl = "",
  blobPath = "",
  fileName = "",
  uploading = false,
  error = "",
  warning = "",
  unsupported = false,
  onPick,
  onClear,
  className = "",
}) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  // See the file header: only a local object URL may be rendered as-is.
  //
  // `previewUrl` leads because it is the file the author literally just
  // picked — no network, no SAS expiry, no authorization. `dataUrl` is only a
  // local URL in legacy callers; from the presign it is a server identity and
  // falls through to the signed lookup below.
  const localSrc = LOCAL_PREVIEW_SCHEME.test(previewUrl)
    ? previewUrl
    : LOCAL_PREVIEW_SCHEME.test(dataUrl)
      ? dataUrl
      : "";

  // `useMediaUrl` takes a normalized attachment. `url` is left empty on
  // purpose — its fallback path is for legacy rows whose `url` really is
  // fetchable, which a broadcast presign's `data_url` is not.
  const attachment = useMemo(() => ({ blobPath, url: "" }), [blobPath]);

  // Hooks run unconditionally, but the lookup is deferred whenever a local
  // preview already exists: minting a SAS for an image the author is looking
  // at is a round trip for nothing.
  const { url: signedUrl, refresh } = useMediaUrl(attachment, {
    enabled: !localSrc && Boolean(blobPath),
  });

  const previewSrc = localSrc || signedUrl;
  const hasImage = Boolean(localSrc || blobPath);

  /*
   * A failed signed lookup is NOT a failed upload.
   *
   * `GET /uploads/media-url` answers 404 for a broadcast image: it authorizes
   * by walking the room message carrying the blob, and a broadcast image is
   * never attached to a message. The bytes are in storage and `image_url` is
   * on the draft regardless — only the thumbnail is missing. With a local
   * preview in hand that lookup never runs, so this matters only for an image
   * restored from a saved record, where the hatch placeholder is the honest
   * fallback rather than a broken <img>.
   */
  const showsThumbnail = hasImage && Boolean(previewSrc);

  /**
   * Forward a file upward, ignoring the empty gestures — a drag that carried
   * no file, or a file dialog the author cancelled.
   *
   * @param {File|undefined} file
   */
  const pick = (file) => {
    if (file && onPick) onPick(file);
  };

  const handleInputChange = (event) => {
    const files = event.target.files;
    pick(files && files[0]);
    // Re-picking the same file after a Remove must fire `change` again; an
    // unchanged value would not.
    event.target.value = "";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    // Ignored while an upload is in flight, so drop and Browse agree — Browse
    // is already `disabled={uploading}`, and two affordances on the same box
    // behaving differently is the kind of inconsistency nobody reports but
    // everybody notices.
    //
    // `useBroadcastImage` also invalidates any in-flight upload on every
    // `pick`, so a drop that slips through here still cannot attach a stale
    // image. This guard is the affordance; that is the correctness.
    if (uploading) return;
    const files = event.dataTransfer && event.dataTransfer.files;
    pick(files && files[0]);
  };

  // The upload scope is missing server-side: state the fact, offer nothing.
  if (unsupported) {
    return (
      <div className={className}>
        <UnsupportedNote />
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        data-testid="bc-image-dropzone"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`${BOX_BASE} ${dragging ? BOX_DRAGGING : BOX_RESTING}`}
      >
        {showsThumbnail ? (
          <img
            src={previewSrc}
            // Meaningful: a screen-reader user checking their own work needs
            // to know a preview is sitting here, even unable to judge it.
            alt="Broadcast preview"
            // Explicit intrinsic dimensions so the box reserves the right
            // aspect ratio before the (possibly remote, possibly signed)
            // image resolves.
            width={IMAGE_WIDTH}
            height={IMAGE_HEIGHT}
            // A SAS that expired while the composer sat open recovers here
            // rather than leaving a broken thumbnail behind.
            onError={refresh}
            className={`${MEDIA_SLOT} rounded-md object-cover`}
          />
        ) : (
          <Hatch className={MEDIA_SLOT} />
        )}

        <div className="min-w-0 flex-1">
          {hasImage ? (
            <div className="truncate text-xs text-bc-text-2" title={fileName}>
              {fileName || "Image attached"}
            </div>
          ) : (
            <div className="text-xs text-bc-soft">Drop an image or browse</div>
          )}
          <div className="mt-1 font-mono text-xs text-bc-faint">{IMAGE_HINT}</div>
          {uploading ? (
            <div className="mt-1 text-xs text-bc-muted">Uploading…</div>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {hasImage ? (
            <button
              type="button"
              onClick={() => onClear && onClear()}
              className={`${BUTTON_BASE} ${BUTTON_ENABLED}`}
            >
              Remove
            </button>
          ) : null}
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current && inputRef.current.click()}
            className={`${BUTTON_BASE} ${uploading ? BUTTON_DISABLED : BUTTON_ENABLED}`}
          >
            Browse
          </button>
        </div>

        <input
          ref={inputRef}
          data-testid="bc-image-input"
          type="file"
          accept={IMAGE_MIME_TYPES.join(",")}
          onChange={handleInputChange}
          className="hidden"
        />
      </div>

      {/* Blocking. Assertive, because the author must act on it. */}
      {error ? (
        <div role="alert" className="mt-1.5 text-xs text-bc-bad">
          {error}
        </div>
      ) : null}

      {/* Advisory, and deliberately NOT an alert: the image uploaded fine.
          Interrupting a screen-reader user to say their picture is the wrong
          size, when nothing is broken and nothing is required of them, is
          noise. It reads in document order like any other helper line. */}
      {warning ? (
        <div className="mt-1.5 text-xs text-bc-warn">{warning}</div>
      ) : null}
    </div>
  );
};

export default ImageDropzone;
